// Derivation helpers for a compliance AI evaluation.
//
// The backend stores and returns the model's Evaluation JSON verbatim, so a
// result carries only what was read from the supplier's document — item_key,
// verdict, found value, message. Everything a human needs to read it (section,
// label, severity, the requirement itself) lives on the checklist item with the
// matching `key`, on the request's stored `checklistExtraction`. Joining the two
// and deriving the overall verdict is deliberately the FE's job: the evaluate
// endpoint returns no overall_status.
//
// Everything here is pure and imports nothing from the fetch layer, so it stays
// testable in isolation. Reading a stored document's bytes back for evaluation
// is I/O and lives with the evaluate thunk instead.

import {
	CHECKLIST_CHECK_TYPES,
	CHECKLIST_ITEM_SEVERITIES,
	CHECKLIST_DOCUMENT_ISSUES,
	CHECKLIST_DOCUMENT_ISSUE_SUPPLIER_MESSAGES,
	CHECKLIST_RESULT_STATUSES,
	ChecklistEvaluation,
	ChecklistEvaluationResult,
	ChecklistExtractionItem,
	EVALUATION_OVERALL_STATUSES,
} from '../types/ComplianceChecklistTypes';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from './DataAccessUtils';
import { formatCurrency } from './DeliverableUtils';

// A result with its checklist item's context folded in. `item` is null when the
// result references a key the stored checklist does not contain.
export interface EnrichedEvaluationResult extends ChecklistEvaluationResult {
	item: ChecklistExtractionItem | null;
	section: string;
	label: string;
	severity: string;
	display_order: number;
	// True for a placeholder row standing in for a checklist item the model never
	// answered. There is no found value to describe on one, so the view says the
	// requirement went unchecked rather than reporting it as blank on the document.
	skipped?: boolean;
}

export interface EvaluationResultSection {
	name: string;
	results: EnrichedEvaluationResult[];
}

const FALLBACK_SECTION_NAME = 'Requirements';

// The evaluate endpoint reads a PDF, and so does the preview alongside the
// results. Some browsers leave `type` empty on a file read back from storage, so
// fall back to the extension rather than refusing a document that is fine.
export const isPdfFile = (file: File): boolean =>
	!!file &&
	(file.type === 'application/pdf' || /\.pdf$/i.test(nullSafeGetOrElse('name', file, '')));

const KNOWN_DOCUMENT_ISSUES = [
	CHECKLIST_DOCUMENT_ISSUES.NONE,
	CHECKLIST_DOCUMENT_ISSUES.WRONG_DOCUMENT_TYPE,
	CHECKLIST_DOCUMENT_ISSUES.UNREADABLE,
	CHECKLIST_DOCUMENT_ISSUES.INCOMPLETE,
	CHECKLIST_DOCUMENT_ISSUES.MULTIPLE_CERTIFICATES,
];

// Same reasoning as an unrecognised result status: document_issue comes back
// verbatim from the model, so a value outside the enum has to mean "a person
// should look at this", never "nothing to report".
export const isUnrecognizedDocumentIssue = (documentIssue: string): boolean =>
	KNOWN_DOCUMENT_ISSUES.indexOf(documentIssue) === -1;

// The supplier-facing line for a document-level problem, or null when there is
// none. An issue we do not recognise still gets a banner — silently dropping it
// would hide the very thing that made the verdict non-green.
export const describeDocumentIssue = (documentIssue: string): string => {
	if (!documentIssue || documentIssue === CHECKLIST_DOCUMENT_ISSUES.NONE) {
		return null;
	}
	return (
		CHECKLIST_DOCUMENT_ISSUE_SUPPLIER_MESSAGES[documentIssue] ||
		'We found a problem with this document that needs a person to look at it.'
	);
};

const KNOWN_RESULT_STATUSES = [
	CHECKLIST_RESULT_STATUSES.SATISFIED,
	CHECKLIST_RESULT_STATUSES.NOT_SATISFIED,
	CHECKLIST_RESULT_STATUSES.NEEDS_HUMAN_REVIEW,
];

// The response is the model's JSON stored verbatim, so a status outside the three
// known verdicts is possible. Treat it as unreadable rather than as a pass: a
// false SATISFIED puts an under-insured supplier on a customer's site.
export const isUnrecognizedResultStatus = (status: string): boolean =>
	KNOWN_RESULT_STATUSES.indexOf(status) === -1;

// Whether a verdict has to go in front of a person — explicitly under review, or
// a status we do not recognise.
export const resultNeedsHumanReview = (status: string): boolean =>
	status === CHECKLIST_RESULT_STATUSES.NEEDS_HUMAN_REVIEW || isUnrecognizedResultStatus(status);

// Last-resort label when a result has no matching checklist item: "gl_each_
// occurrence_min" reads as "Gl each occurrence min". Ugly but honest — better
// than showing a raw key or dropping the row.
const humanizeItemKey = (itemKey: string): string => {
	const spaced = nullSafeGetOrElse('length', itemKey, 0) > 0 ? itemKey.replace(/_/g, ' ') : '';
	return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};

// Same question for a document we only have a URL for. The stored fileName is the
// nicer signal, but a URL is all some call sites hold, so read the path.
export const isPdfUrl = (fileUrl: string): boolean =>
	!!fileUrl && /\.pdf(\?|#|$)/i.test(fileUrl.split('?')[0]);

// Keyed by the item's `key`, which is arbitrary user/model-supplied text — always
// bracket-index this map, never a nullSafeGet path (a key holding a period would
// silently mis-resolve).
const buildItemsByKey = (checklistItems: ChecklistExtractionItem[]) => {
	const itemsByKey: { [itemKey: string]: ChecklistExtractionItem } = {};
	(checklistItems || []).forEach((item) => {
		const itemKey = nullSafeGet('key', item);
		if (itemKey) {
			itemsByKey[itemKey] = item;
		}
	});
	return itemsByKey;
};

// Folds each result together with its checklist item. An unmatched result is
// treated as REQUIRED so a checklist we cannot read never downgrades a failure
// into something that looks optional.
export const joinResultsToChecklistItems = (
	evaluation: ChecklistEvaluation,
	checklistItems: ChecklistExtractionItem[]
): EnrichedEvaluationResult[] => {
	const results = nullSafeGetOrElse('results', evaluation, []);
	const itemsByKey = buildItemsByKey(checklistItems);
	return results.map((result: ChecklistEvaluationResult, index: number) => {
		const item = itemsByKey[result.item_key] || null;
		return {
			...result,
			item,
			section: item
				? nullSafeGetOrElse('section', item, FALLBACK_SECTION_NAME)
				: FALLBACK_SECTION_NAME,
			label: item ? nullSafeGetOrElse('label', item, '') : humanizeItemKey(result.item_key),
			severity: item
				? nullSafeGetOrElse('severity', item, CHECKLIST_ITEM_SEVERITIES.REQUIRED)
				: CHECKLIST_ITEM_SEVERITIES.REQUIRED,
			// Results arrive in checklist order, so the index is a faithful fallback
			// when the item (and its display_order) is missing.
			display_order: item ? nullSafeGetOrElse('display_order', item, index + 1) : index + 1,
		};
	});
};

// Placeholder rows for checklist items the model returned no result for. The
// overall verdict already routes a partial answer to human review and the tallies
// already count the skipped items, so the detailed view has to be able to show a
// row for each: otherwise it reports "2 need a closer look" above a list that
// names only one, and nobody can tell which requirement went unchecked.
//
// Ordered and enriched exactly like a real result so grouping and sorting need no
// special case; `skipped` is what tells the view not to describe a found value.
export const buildSkippedResultRows = (
	evaluation: ChecklistEvaluation,
	checklistItems: ChecklistExtractionItem[]
): EnrichedEvaluationResult[] => {
	const answered = joinResultsToChecklistItems(evaluation, checklistItems);
	// A document the model refused outright (wrong type, unreadable) comes back with
	// no results by design — the same case summarizeEvaluationResults zeroes out.
	// Listing every requirement as unchecked would imply each one is queued for a
	// reviewer, when the document-level issue is the whole story.
	if (answered.length === 0) {
		return [];
	}
	const answeredKeys = new Set(answered.map((result) => result.item_key));
	return (checklistItems || [])
		.filter((item) => !answeredKeys.has(nullSafeGet('key', item)))
		.map((item, index) => ({
			item_key: nullSafeGetOrElse('key', item, ''),
			// Never a pass: an item the model declined to answer is exactly what a
			// person has to look at.
			status: CHECKLIST_RESULT_STATUSES.NEEDS_HUMAN_REVIEW,
			location: null,
			confidence: null,
			item,
			section: nullSafeGetOrElse('section', item, FALLBACK_SECTION_NAME),
			label: nullSafeGetOrElse('label', item, humanizeItemKey(nullSafeGet('key', item))),
			severity: nullSafeGetOrElse('severity', item, CHECKLIST_ITEM_SEVERITIES.REQUIRED),
			// Falls after everything answered when the item carries no order of its own.
			display_order: nullSafeGetOrElse('display_order', item, answered.length + index + 1),
			skipped: true,
		}));
};

// True when the model returned a result for every checklist item and nothing
// else. The spec's code-side guard routes a mismatch to human review rather than
// trusting a partial answer, since a silently dropped REQUIRED item would
// otherwise read as compliant.
const resultKeysMatchChecklist = (
	evaluation: ChecklistEvaluation,
	checklistItems: ChecklistExtractionItem[]
): boolean => {
	const items = checklistItems || [];
	if (items.length === 0) {
		return true;
	}
	const results = nullSafeGetOrElse('results', evaluation, []);
	const resultKeys = new Set(results.map((result) => result.item_key));
	if (resultKeys.size !== results.length || resultKeys.size !== items.length) {
		return false;
	}
	return items.every((item) => resultKeys.has(nullSafeGet('key', item)));
};

// The overall verdict, computed here because the model does not return one:
// any REQUIRED item NOT_SATISFIED wins, then any REQUIRED item under review,
// otherwise compliant. RECOMMENDED items never affect it.
export const deriveOverallEvaluationStatus = (
	evaluation: ChecklistEvaluation,
	checklistItems: ChecklistExtractionItem[]
): string => {
	if (!evaluation) {
		return null;
	}
	const documentIssue = nullSafeGetOrElse(
		'document_issue',
		evaluation,
		CHECKLIST_DOCUMENT_ISSUES.NONE
	);
	// A document we could not read or that is the wrong type fails outright —
	// there is nothing to review, the supplier has to send something else.
	if (
		documentIssue === CHECKLIST_DOCUMENT_ISSUES.WRONG_DOCUMENT_TYPE ||
		documentIssue === CHECKLIST_DOCUMENT_ISSUES.UNREADABLE
	) {
		return EVALUATION_OVERALL_STATUSES.NON_COMPLIANT;
	}
	const enriched = joinResultsToChecklistItems(evaluation, checklistItems);
	const isRequired = (result: EnrichedEvaluationResult) =>
		result.severity !== CHECKLIST_ITEM_SEVERITIES.RECOMMENDED;
	if (
		enriched.some(
			(result) => isRequired(result) && result.status === CHECKLIST_RESULT_STATUSES.NOT_SATISFIED
		)
	) {
		return EVALUATION_OVERALL_STATUSES.NON_COMPLIANT;
	}
	// Missing pages/endorsements or a multi-certificate upload always needs a
	// person, even when everything the model could check passed.
	if (
		documentIssue === CHECKLIST_DOCUMENT_ISSUES.INCOMPLETE ||
		documentIssue === CHECKLIST_DOCUMENT_ISSUES.MULTIPLE_CERTIFICATES ||
		isUnrecognizedDocumentIssue(documentIssue) ||
		!resultKeysMatchChecklist(evaluation, checklistItems) ||
		enriched.some((result) => isRequired(result) && resultNeedsHumanReview(result.status))
	) {
		return EVALUATION_OVERALL_STATUSES.NEEDS_HUMAN_REVIEW;
	}
	return EVALUATION_OVERALL_STATUSES.COMPLIANT;
};

// Failures first inside a section: what the supplier has to act on should not be
// buried under the requirements that already pass.
const STATUS_SORT_PRIORITY = {
	[CHECKLIST_RESULT_STATUSES.NOT_SATISFIED]: 0,
	[CHECKLIST_RESULT_STATUSES.NEEDS_HUMAN_REVIEW]: 1,
	[CHECKLIST_RESULT_STATUSES.SATISFIED]: 2,
};

// Bracket-indexed, with an explicit fallback for a status the model invents.
const statusRank = (result: EnrichedEvaluationResult): number => {
	const rank = STATUS_SORT_PRIORITY[result.status];
	return rank === undefined ? 3 : rank;
};

const severityRank = (result: EnrichedEvaluationResult): number =>
	result.severity === CHECKLIST_ITEM_SEVERITIES.RECOMMENDED ? 1 : 0;

const compareResults = (
	firstResult: EnrichedEvaluationResult,
	secondResult: EnrichedEvaluationResult
): number => {
	const bySeverity = severityRank(firstResult) - severityRank(secondResult);
	if (bySeverity !== 0) {
		return bySeverity;
	}
	const byStatus = statusRank(firstResult) - statusRank(secondResult);
	if (byStatus !== 0) {
		return byStatus;
	}
	return firstResult.display_order - secondResult.display_order;
};

// Sections in the order they first appear on the source form, REQUIRED before
// RECOMMENDED within each.
export const groupEvaluationResultsBySection = (
	enrichedResults: EnrichedEvaluationResult[]
): EvaluationResultSection[] => {
	const sections: EvaluationResultSection[] = [];
	const sectionsByName: { [sectionName: string]: EvaluationResultSection } = {};
	[...(enrichedResults || [])]
		.sort((firstResult, secondResult) => firstResult.display_order - secondResult.display_order)
		.forEach((result) => {
			const sectionName = result.section || FALLBACK_SECTION_NAME;
			if (!sectionsByName[sectionName]) {
				sectionsByName[sectionName] = { name: sectionName, results: [] };
				sections.push(sectionsByName[sectionName]);
			}
			sectionsByName[sectionName].results.push(result);
		});
	sections.forEach((section) => section.results.sort(compareResults));
	return sections;
};

export interface EvaluationResultCounts {
	met: number;
	notMet: number;
	needsReview: number;
	total: number;
}

// One place that turns an evaluation into "x of y met". Used by the full results
// view and by the compact row summaries on both sides, so a supplier and their
// client can never be shown different tallies for the same stored evaluation.
//
// Counts are bucketed the same way the overall verdict is derived: `met` counts
// SATISFIED explicitly rather than by subtraction, so a status the model invented
// lands in `needsReview` instead of silently reading as a pass.
export const summarizeEvaluationResults = (
	evaluation: ChecklistEvaluation,
	checklistItems: ChecklistExtractionItem[]
): EvaluationResultCounts => {
	const enriched = joinResultsToChecklistItems(evaluation, checklistItems);
	// A document the model refused outright (wrong type, unreadable) comes back with
	// no results by design. The document-level issue explains that; reporting every
	// requirement as "to check" would imply they are queued for a reviewer.
	if (enriched.length === 0) {
		return { met: 0, notMet: 0, needsReview: 0, total: 0 };
	}
	const met = enriched.filter(
		(result) => result.status === CHECKLIST_RESULT_STATUSES.SATISFIED
	).length;
	const notMet = enriched.filter(
		(result) => result.status === CHECKLIST_RESULT_STATUSES.NOT_SATISFIED
	).length;
	const needsReview = enriched.filter((result) => resultNeedsHumanReview(result.status)).length;
	// A checklist item the model simply skipped must still be counted, or the totals
	// contradict the verdict: resultKeysMatchChecklist already flips the overall to
	// NEEDS_HUMAN_REVIEW, and "all 2 met" beside that reads as if it justified it.
	// Counted off the same rows the detailed view renders, so a tally can never name
	// more items than the sections below it list.
	const skipped = buildSkippedResultRows(evaluation, checklistItems).length;
	return {
		met,
		notMet,
		needsReview: needsReview + skipped,
		total: enriched.length + skipped,
	};
};

const AMOUNT_CHECK_TYPES = [CHECKLIST_CHECK_TYPES.MIN_AMOUNT, CHECKLIST_CHECK_TYPES.MAX_AMOUNT];

// What the model read out of the document, in the units the requirement is
// written in. A blank field is a finding in its own right, so say so rather than
// rendering an empty line.
//
// found_number is read zero-preserving on purpose: a limit of 0 means "no
// coverage", which is a different finding from a field nobody filled in, and
// plain nullSafeGet drops it on a truthiness test.
export const describeFoundValue = (result: EnrichedEvaluationResult): string => {
	const foundText = nullSafeGet('found_text', result);
	if (foundText) {
		return foundText;
	}
	const foundNumber = nullSafeGetWithZero('found_number', result);
	if (foundNumber !== undefined && foundNumber !== null) {
		const checkType = nullSafeGet('item.check_type', result);
		return AMOUNT_CHECK_TYPES.indexOf(checkType) !== -1
			? formatCurrency(foundNumber)
			: `${foundNumber}`;
	}
	return 'blank';
};
